/**
 * API Service - Handles all HTTP requests to the backend
 */

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? `${process.env.REACT_APP_API_URL}/api`
  : (process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api');

// Auth is cookie-based (httpOnly token cookie set by backend)
const getToken = () => null;

// Helper function to make authenticated requests
const authFetch = async (endpoint, options = {}) => {
  const token = getToken();
  
  const config = {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Something went wrong');
  }

  return data;
};

// Helper function for public (non-authenticated) requests
const publicFetch = async (endpoint, options = {}) => {
  const config = {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Something went wrong');
  }

  return data;
};

// ============ AUTH API ============
export const authAPI = {
  // Public auth routes
  register: async (userData) => {
    return publicFetch('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  login: async (credentials) => {
    return publicFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  // Protected auth routes
  getProfile: async () => {
    return authFetch('/auth/me');
  },

  logout: async () => {
    return authFetch('/auth/logout', {
      method: 'POST',
    });
  },

  updateProfile: async (userData) => {
    return authFetch('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  },

  changePassword: async (passwordData) => {
    return authFetch('/auth/password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  },

  forgotPassword: async (email) => {
    return publicFetch('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  resetPassword: async (token, password) => {
    return publicFetch(`/auth/reset-password/${token}`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  },
};

// ============ PRODUCTS API ============
export const productsAPI = {
  // Public routes - no auth required
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return publicFetch('/products' + (queryString ? '?' + queryString : ''));
  },

  getById: async (id) => {
    return publicFetch('/products/' + id);
  },

  getByCategory: async (category) => {
    return publicFetch('/products?category=' + category);
  },

  getCategories: async () => {
    return publicFetch('/products/categories');
  },

  getCategoryCounts: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return publicFetch('/products/category-counts' + (queryString ? '?' + queryString : ''));
  },

  // Admin routes - require auth
  create: async (productData) => {
    return authFetch('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  },

  update: async (id, productData) => {
    return authFetch('/products/' + id, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  },

  delete: async (id) => {
    return authFetch('/products/' + id, {
      method: 'DELETE',
    });
  },
};

// ============ UPLOADS API ============
export const uploadsAPI = {
  uploadProductImage: async (file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_BASE_URL}/uploads/product-image`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload image');
    }

    return data;
  },

  uploadDocument: async (file) => {
    const token = getToken();
    const formData = new FormData();
    formData.append('document', file);

    const response = await fetch(`${API_BASE_URL}/uploads/document`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || 'Failed to upload document');
    }

    return data;
  },
};

// ============ CATEGORIES API ============
export const categoriesAPI = {
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return publicFetch('/categories' + (queryString ? '?' + queryString : ''));
  },
  getFlat: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return publicFetch('/categories/flat' + (queryString ? '?' + queryString : ''));
  },
  create: async (data) => {
    return authFetch('/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    return authFetch('/categories/' + id, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    return authFetch('/categories/' + id, {
      method: 'DELETE',
    });
  },
};

// ============ SETTINGS API ============
export const settingsAPI = {
  getPublic: async () => {
    return publicFetch('/settings/public');
  },
  getAdmin: async () => {
    return authFetch('/settings');
  },
  update: async (data) => {
    return authFetch('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ============ QUOTES API ============
export const quotesAPI = {
  // Create quote (works for both logged-in users and guests)
  create: async (quoteData) => {
    const token = getToken();
    // Use authFetch if logged in, publicFetch if guest
    if (token) {
      return authFetch('/quotes', {
        method: 'POST',
        body: JSON.stringify(quoteData),
      });
    }
    return publicFetch('/quotes', {
      method: 'POST',
      body: JSON.stringify(quoteData),
    });
  },

  getMyQuotes: async () => {
    return authFetch('/quotes');
  },

  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/quotes/admin/all' + (queryString ? '?' + queryString : ''));
  },

  getById: async (id) => {
    return authFetch('/quotes/' + id);
  },

  updateStatus: async (id, data) => {
    return authFetch(`/quotes/admin/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  addDocument: async (id, docData) => {
    return authFetch(`/quotes/admin/${id}/documents`, {
      method: 'POST',
      body: JSON.stringify(docData),
    });
  },

  removeDocument: async (id, docId) => {
    return authFetch(`/quotes/admin/${id}/documents/${docId}`, {
      method: 'DELETE',
    });
  },
};

// ============ PROJECTS API ============
export const projectsAPI = {
  getMyProjects: async () => {
    return authFetch('/projects');
  },

  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/projects/admin/all' + (queryString ? '?' + queryString : ''));
  },

  getById: async (id) => {
    return authFetch('/projects/' + id);
  },

  getTimeline: async (id) => {
    return authFetch('/projects/' + id + '/timeline');
  },

  addDocument: async (id, docData) => {
    return authFetch(`/projects/admin/${id}/documents`, {
      method: 'POST',
      body: JSON.stringify(docData),
    });
  },

  removeDocument: async (id, docId) => {
    return authFetch(`/projects/admin/${id}/documents/${docId}`, {
      method: 'DELETE',
    });
  },
};

// ============ ORDERS API ============
export const ordersAPI = {
  create: async (orderData) => {
    return authFetch('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  getMyOrders: async () => {
    return authFetch('/orders');
  },

  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/orders/admin/all' + (queryString ? '?' + queryString : ''));
  },

  getById: async (id) => {
    return authFetch('/orders/' + id);
  },

  track: async (id) => {
    return authFetch('/orders/' + id + '/track');
  },

  // Submit manual payment details for verification
  submitPayment: async (orderId, paymentData) => {
    return authFetch('/orders/' + orderId + '/submit-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },

  // Admin: Update order
  update: async (orderId, data) => {
    return authFetch('/orders/admin/' + orderId, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Admin: Update payment status (verify payment)
  updatePayment: async (orderId, paymentData) => {
    return authFetch('/orders/admin/' + orderId + '/payment', {
      method: 'PUT',
      body: JSON.stringify(paymentData),
    });
  },

  // Admin: Convert approved quote to order
  convertQuoteToOrder: async (data) => {
    return authFetch('/orders/admin/convert-quote', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Admin: Send guest payment link email
  sendGuestPaymentLinkEmail: async (orderId) => {
    return authFetch('/orders/admin/' + orderId + '/send-payment-link', {
      method: 'POST',
    });
  },

  // Admin: Send guest invoice link email
  sendGuestInvoiceLinkEmail: async (orderId) => {
    return authFetch('/orders/admin/' + orderId + '/send-invoice-link', {
      method: 'POST',
    });
  },

  // Guest: Get order by payment token
  getGuestByToken: async (token) => {
    return publicFetch('/orders/guest/' + token);
  },

  // Guest: Submit payment details
  submitGuestPayment: async (token, paymentData) => {
    return publicFetch('/orders/guest/' + token + '/submit-payment', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },
};

// ============ USER API ============
export const userAPI = {
  getDashboard: async () => {
    return authFetch('/users/dashboard');
  },

  getStats: async () => {
    return authFetch('/users/stats');
  },

  // Admin user management
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/users/admin/all' + (queryString ? '?' + queryString : ''));
  },

  create: async (userData) => {
    return authFetch('/users/admin', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  update: async (id, userData) => {
    return authFetch('/users/admin/' + id, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  },

  delete: async (id) => {
    return authFetch('/users/admin/' + id, {
      method: 'DELETE',
    });
  },
};

// ============ PDF API ============
export const pdfAPI = {
  // Download invoice PDF
  downloadInvoice: async (orderId) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/pdf/invoice/${orderId}`, {
      credentials: 'include',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to download invoice');
    }
    return response.blob();
  },

  // Download guest invoice PDF (public)
  downloadGuestInvoice: async (token) => {
    const response = await fetch(`${API_BASE_URL}/pdf/guest-invoice/${token}`, {
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to download invoice');
    }
    return response.blob();
  },

  // Download quote PDF
  downloadQuote: async (quoteId) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/pdf/quote/${quoteId}`, {
      credentials: 'include',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to download quote');
    }
    return response.blob();
  },

  // Download Bill of Materials PDF
  downloadBOM: async (projectId) => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/pdf/bom/${projectId}`, {
      credentials: 'include',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to download BOM');
    }
    return response.blob();
  },

  // Download sales report (Admin)
  downloadSalesReport: async (startDate, endDate) => {
    const token = getToken();
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await fetch(`${API_BASE_URL}/pdf/report/sales?${params.toString()}`, {
      credentials: 'include',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to download sales report');
    }
    return response.blob();
  },

  // Download inventory report (Admin)
  downloadInventoryReport: async () => {
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/pdf/report/inventory`, {
      credentials: 'include',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to download inventory report');
    }
    return response.blob();
  },
};

// ============ INVENTORY API ============
export const inventoryAPI = {
  getOverview: async () => {
    return authFetch('/products/inventory/overview');
  },

  getLowStock: async () => {
    return authFetch('/products/inventory/low-stock');
  },

  updateStock: async (productId, data) => {
    return authFetch('/products/' + productId + '/stock', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  bulkUpdateStock: async (updates) => {
    return authFetch('/products/inventory/bulk-update', {
      method: 'PUT',
      body: JSON.stringify({ updates }),
    });
  },

  getStockHistory: async (productId) => {
    return authFetch('/products/' + productId + '/stock-history');
  },

  updateSupplier: async (productId, data) => {
    return authFetch('/products/' + productId + '/supplier', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  updateReorderSettings: async (productId, data) => {
    return authFetch('/products/' + productId + '/reorder-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ============ ADMIN API ============
export const adminAPI = {
  getDashboard: async () => {
    return authFetch('/users/admin/dashboard');
  },

  getUsers: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/users/admin/all' + (queryString ? '?' + queryString : ''));
  },

  getUser: async (id) => {
    return authFetch('/users/admin/' + id);
  },

  createUser: async (userData) => {
    return authFetch('/users/admin', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  updateUser: async (id, userData) => {
    return authFetch('/users/admin/' + id, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  },

  deleteUser: async (id) => {
    return authFetch('/users/admin/' + id, {
      method: 'DELETE',
    });
  },

  getQuotes: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/quotes/admin/all' + (queryString ? '?' + queryString : ''));
  },

  getOrders: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/orders/admin/all' + (queryString ? '?' + queryString : ''));
  },

  getProjects: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/projects/admin/all' + (queryString ? '?' + queryString : ''));
  },
};

// ============ PAYMENTS API ============
export const paymentsAPI = {
  // Get available payment methods
  getMethods: async (region = 'local') => {
    return authFetch(`/payments/methods?region=${region}`);
  },

  // Get order payment details
  getOrderPayments: async (orderId) => {
    return authFetch(`/payments/order/${orderId}`);
  },
};

// ============ WISHLIST API ============
export const wishlistAPI = {
  // Get user's wishlist
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/wishlist' + (queryString ? '?' + queryString : ''));
  },

  // Get wishlist product IDs (for quick check)
  getIds: async () => {
    return authFetch('/wishlist/ids');
  },

  // Get wishlist count
  getCount: async () => {
    return authFetch('/wishlist/count');
  },

  // Check if product is in wishlist
  check: async (productId) => {
    return authFetch(`/wishlist/check/${productId}`);
  },

  // Add product to wishlist
  add: async (productId, options = {}) => {
    return authFetch('/wishlist', {
      method: 'POST',
      body: JSON.stringify({ productId, ...options }),
    });
  },

  // Toggle product in wishlist (add/remove)
  toggle: async (productId) => {
    return authFetch(`/wishlist/toggle/${productId}`, {
      method: 'POST',
    });
  },

  // Update wishlist item
  update: async (id, data) => {
    return authFetch(`/wishlist/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Remove from wishlist by wishlist ID
  remove: async (id) => {
    return authFetch(`/wishlist/${id}`, {
      method: 'DELETE',
    });
  },

  // Remove from wishlist by product ID
  removeByProduct: async (productId) => {
    return authFetch(`/wishlist/product/${productId}`, {
      method: 'DELETE',
    });
  },

  // Clear entire wishlist
  clear: async () => {
    return authFetch('/wishlist/clear', {
      method: 'DELETE',
    });
  },
};

// ============ REVIEWS API ============
export const reviewsAPI = {
  // Get reviews for a product (public)
  getProductReviews: async (productId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return publicFetch(`/reviews/product/${productId}` + (queryString ? '?' + queryString : ''));
  },

  // Get user's reviews
  getMyReviews: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/reviews/my-reviews' + (queryString ? '?' + queryString : ''));
  },

  // Check if user can review a product
  canReview: async (productId) => {
    return authFetch(`/reviews/can-review/${productId}`);
  },

  // Create a review
  create: async (reviewData) => {
    return authFetch('/reviews', {
      method: 'POST',
      body: JSON.stringify(reviewData),
    });
  },

  // Update a review
  update: async (id, reviewData) => {
    return authFetch(`/reviews/${id}`, {
      method: 'PUT',
      body: JSON.stringify(reviewData),
    });
  },

  // Delete a review
  delete: async (id) => {
    return authFetch(`/reviews/${id}`, {
      method: 'DELETE',
    });
  },

  // Mark review as helpful
  markHelpful: async (id) => {
    return authFetch(`/reviews/${id}/helpful`, {
      method: 'POST',
    });
  },

  // Report a review
  report: async (id, reason) => {
    return authFetch(`/reviews/${id}/report`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  },

  // Admin: Get all reviews
  adminGetAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/reviews/admin/all' + (queryString ? '?' + queryString : ''));
  },

  // Admin: Update review status
  adminUpdateStatus: async (id, statusData) => {
    return authFetch(`/reviews/admin/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify(statusData),
    });
  },

  // Admin: Add response to review
  adminRespond: async (id, response) => {
    return authFetch(`/reviews/admin/${id}/respond`, {
      method: 'PUT',
      body: JSON.stringify({ response }),
    });
  },

  // Admin: Delete review
  adminDelete: async (id) => {
    return authFetch(`/reviews/admin/${id}`, {
      method: 'DELETE',
    });
  },
};

// ============ CONSULTATIONS API ============
export const consultationsAPI = {
  // Submit a consultation request (public with optional auth)
  // Uses FormData to support file uploads
  submit: async (data, files = []) => {
    const formData = new FormData();
    
    // Add all text fields
    formData.append('projectType', data.projectType);
    formData.append('styles', JSON.stringify(data.styles || []));
    formData.append('hasDesigns', data.hasDesigns || '');
    formData.append('fileCategories', JSON.stringify(data.fileCategories || []));
    formData.append('fileDescription', data.fileDescription || '');
    formData.append('location', data.location);
    formData.append('projectSize', data.projectSize || '');
    formData.append('timeline', data.timeline);
    formData.append('priorities', JSON.stringify(data.priorities || []));
    formData.append('additionalNotes', data.additionalNotes || '');
    formData.append('contactName', data.contactName);
    formData.append('contactEmail', data.contactEmail);
    formData.append('contactPhone', data.contactPhone);
    formData.append('preferredContact', data.preferredContact || '');
    formData.append('bestTimeToContact', data.bestTimeToContact || '');
    
    // Add files
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('files', file);
      });
    }
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/consultations`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
        // Don't set Content-Type - browser will set it with boundary for FormData
      },
      body: formData,
    });
    
    const responseData = await response.json();
    
    if (!response.ok) {
      throw new Error(responseData.message || 'Failed to submit consultation');
    }
    
    return responseData;
  },

  // Get user's consultations (requires auth)
  getMyConsultations: async () => {
    return authFetch('/consultations/my');
  },

  // Get single consultation
  getById: async (id) => {
    return authFetch(`/consultations/${id}`);
  },

  // Admin: Get all consultations
  adminGetAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/consultations' + (queryString ? '?' + queryString : ''));
  },

  // Admin: Get statistics
  adminGetStats: async () => {
    return authFetch('/consultations/stats');
  },

  // Admin: Update consultation
  adminUpdate: async (id, data) => {
    return authFetch(`/consultations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ============ BANNERS API ============
export const bannersAPI = {
  getPublic: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return publicFetch('/banners/public' + (queryString ? '?' + queryString : ''));
  },
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return authFetch('/banners' + (queryString ? '?' + queryString : ''));
  },
  getById: async (id) => {
    return authFetch(`/banners/${id}`);
  },
  create: async (data) => {
    return authFetch('/banners', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    return authFetch(`/banners/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    return authFetch(`/banners/${id}`, { method: 'DELETE' });
  },
  reorder: async (orderedIds) => {
    return authFetch('/banners/reorder', {
      method: 'PUT',
      body: JSON.stringify({ orderedIds }),
    });
  },
  uploadMedia: async (file) => {
    const formData = new FormData();
    formData.append('media', file);
    const response = await fetch(
      `${API_BASE_URL}/uploads/banner-media`,
      {
        method: 'POST',
        credentials: 'include',
        body: formData,
      }
    );
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'Upload failed');
    return data;
  },
};

const api = {
  auth: authAPI,
  products: productsAPI,
  quotes: quotesAPI,
  projects: projectsAPI,
  orders: ordersAPI,
  user: userAPI,
  admin: adminAPI,
  inventory: inventoryAPI,
  pdf: pdfAPI,
  payments: paymentsAPI,
  wishlist: wishlistAPI,
  reviews: reviewsAPI,
  consultations: consultationsAPI,
  banners: bannersAPI,
};

export default api;
